import React, { useState, useMemo } from 'react';
import { Calculator, TrendingUp, Clock, DollarSign, Users, Zap } from 'lucide-react';

const ROICalculator = ({ isDark }) => {
  const [inputs, setInputs] = useState({
    patientsPerDay: 30,
    doctors: 5,
    avgConsultTime: 15,
    adminStaff: 3,
    avgRevenuePerVisit: 150
  });

  const results = useMemo(() => {
    const { patientsPerDay, doctors, avgConsultTime, adminStaff, avgRevenuePerVisit } = inputs;
    
    const docTimePerDay = avgConsultTime * patientsPerDay;
    const timeSavedPerDoc = docTimePerDay * 0.35;
    const totalTimeSavedDaily = timeSavedPerDoc * doctors;
    const additionalPatients = Math.floor((totalTimeSavedDaily / avgConsultTime) * 0.6);
    const monthlyAdditionalRevenue = additionalPatients * avgRevenuePerVisit * 22;
    const adminCostReduction = adminStaff * 0.5 * 4000;
    const noShowRecovery = patientsPerDay * 0.15 * 0.4 * avgRevenuePerVisit * 22;
    const totalMonthlyBenefit = monthlyAdditionalRevenue + adminCostReduction + noShowRecovery;
    
    return {
      timeSavedDaily: Math.round(totalTimeSavedDaily),
      timeSavedMonthly: Math.round(totalTimeSavedDaily * 22),
      additionalPatients: additionalPatients * 22,
      revenueUplift: Math.round(monthlyAdditionalRevenue),
      adminSavings: Math.round(adminCostReduction),
      noShowRecovery: Math.round(noShowRecovery),
      totalBenefit: Math.round(totalMonthlyBenefit),
      docEfficiency: 35,
      throughputIncrease: Math.round((additionalPatients / patientsPerDay) * 100)
    };
  }, [inputs]);

  const handleInputChange = (key, value) => {
    setInputs(prev => ({ ...prev, [key]: Number(value) }));
  };

  return (
    <section 
      id="calculator" 
      className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-gradient-to-b from-slate-100 to-white'}`}
      data-testid="roi-calculator-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
          }`}>
            ROI Calculator
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            Calculate Your SkyDoc ROI
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            See how much time and revenue SkyDoc can unlock for your practice.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Panel */}
          <div className={`rounded-2xl border p-6 lg:p-8 ${
            isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200 shadow-sm'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDark ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <Calculator className="w-5 h-5 text-blue-500" />
              </div>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Your Practice Details
              </h3>
            </div>

            <div className="space-y-6">
              <InputSlider label="Patients per day" value={inputs.patientsPerDay} min={10} max={200} step={5} unit="patients" icon={Users} isDark={isDark} onChange={(v) => handleInputChange('patientsPerDay', v)} />
              <InputSlider label="Number of doctors" value={inputs.doctors} min={1} max={50} step={1} unit="doctors" icon={Users} isDark={isDark} onChange={(v) => handleInputChange('doctors', v)} />
              <InputSlider label="Avg. consultation time" value={inputs.avgConsultTime} min={5} max={30} step={1} unit="minutes" icon={Clock} isDark={isDark} onChange={(v) => handleInputChange('avgConsultTime', v)} />
              <InputSlider label="Admin staff" value={inputs.adminStaff} min={1} max={20} step={1} unit="staff" icon={Users} isDark={isDark} onChange={(v) => handleInputChange('adminStaff', v)} />
              <InputSlider label="Avg. revenue per visit" value={inputs.avgRevenuePerVisit} min={50} max={500} step={10} unit="$" icon={DollarSign} isDark={isDark} onChange={(v) => handleInputChange('avgRevenuePerVisit', v)} />
            </div>
          </div>

          {/* Results Panel */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 lg:p-8 text-white">
              <p className="text-blue-200 text-sm font-medium mb-2">Estimated Monthly Benefit</p>
              <p className="text-4xl lg:text-5xl font-bold mb-4">${results.totalBenefit.toLocaleString()}</p>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-300" />
                <span className="text-blue-100">Based on your practice inputs</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <ResultCard icon={Clock} label="Time Saved" value={`${results.timeSavedDaily} min/day`} subtext={`${Math.round(results.timeSavedMonthly / 60)} hours/month`} color="blue" isDark={isDark} />
              <ResultCard icon={Users} label="Extra Capacity" value={`+${results.additionalPatients}`} subtext="patients/month" color="emerald" isDark={isDark} />
              <ResultCard icon={DollarSign} label="Revenue Uplift" value={`$${results.revenueUplift.toLocaleString()}`} subtext="from increased throughput" color="purple" isDark={isDark} />
              <ResultCard icon={Zap} label="Admin Savings" value={`$${results.adminSavings.toLocaleString()}`} subtext="reduced workload" color="amber" isDark={isDark} />
            </div>

            <div className={`rounded-xl p-6 border ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
              <p className={`text-sm font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Impact Summary</p>
              <div className="space-y-3">
                <ImpactRow label="Documentation time reduction" value={`${results.docEfficiency}%`} isDark={isDark} />
                <ImpactRow label="Patient throughput increase" value={`+${results.throughputIncrease}%`} isDark={isDark} />
                <ImpactRow label="No-show revenue recovery" value={`$${results.noShowRecovery.toLocaleString()}/mo`} isDark={isDark} />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className={`mb-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            Want a detailed ROI analysis for your organization?
          </p>
          <button className="btn-primary" data-testid="roi-get-analysis-btn">Get Custom Analysis</button>
        </div>
      </div>
    </section>
  );
};

const InputSlider = ({ label, value, min, max, step, unit, icon: Icon, isDark, onChange }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
        <span className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{label}</span>
      </div>
      <span className="text-sm font-bold text-blue-500">
        {unit === '$' ? `$${value}` : `${value} ${unit}`}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full h-2 rounded-lg appearance-none cursor-pointer accent-blue-600 ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
      data-testid={`slider-${label.toLowerCase().replace(/\s+/g, '-')}`}
    />
    <div className={`flex justify-between text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
      <span>{unit === '$' ? `$${min}` : min}</span>
      <span>{unit === '$' ? `$${max}` : max}</span>
    </div>
  </div>
);

const ResultCard = ({ icon: Icon, label, value, subtext, color, isDark }) => {
  const colors = {
    blue: isDark ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-600',
    emerald: isDark ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-600',
    purple: isDark ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-600',
    amber: isDark ? 'bg-amber-900/50 text-amber-400' : 'bg-amber-100 text-amber-600'
  };

  return (
    <div className={`rounded-xl border p-4 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${colors[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
      <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{value}</p>
      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{subtext}</p>
    </div>
  );
};

const ImpactRow = ({ label, value, isDark }) => (
  <div className="flex items-center justify-between">
    <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{label}</span>
    <span className="text-sm font-semibold text-emerald-500">{value}</span>
  </div>
);

export default ROICalculator;
